import React, { useState, useEffect, useContext } from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaFileInvoice,
  FaEye,
  FaDownload,
  FaFilter,
} from "react-icons/fa";

import { FileText, Plus } from "lucide-react";

import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import { ThemeContext } from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

import { useNavigate } from "react-router-dom";

const GRNReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);

  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();

  /* =========================================================
     UI STATE
  ========================================================= */

  const [showForm, setShowForm] = useState(true);

  const [loading, setLoading] = useState(false);

  /* =========================================================
     FORM STATE
  ========================================================= */

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  const [grnNo, setGrnNo] = useState("");

  const [projectCode, setProjectCode] = useState("");

  const [status, setStatus] = useState("");

  const [store, setStore] = useState("");

  const [supplier, setSupplier] = useState("");

  /* =========================================================
     DATA STATE
  ========================================================= */

  const [branchList, setBranchList] = useState([]);

  const [supplierList, setSupplierList] = useState([]);

  const [data, setData] = useState([]);

  /* =========================================================
     LOCAL STORAGE VALUES
  ========================================================= */

  const storedBranch = localStorage.getItem("branch") || "";

  const storedBranchId = localStorage.getItem("branchId") || "";

  const storedBranchType = (
    localStorage.getItem("branchType") || ""
  ).toUpperCase();

  const initialBranchType =
    storedBranch === "HEAD-OFFICE" || storedBranchType === "HO"
      ? "HO"
      : "Branch";

  const [branchType, setBranchType] = useState(initialBranchType);

  /* =========================================================
     COMMON DESIGN CLASSES
     SAME STYLE AS DEPARTMENT MASTER
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${isDark ? "text-slate-300" : "text-slate-700"}
  `;

  /* =========================================================
     FETCH DROPDOWNS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    axios
      .get(
        `https://nissi-india.com/api/po_fillDropdowns?branchid=${
          storedBranchId || 0
        }&branchtype=${branchType === "HO" ? "HO" : "BRANCH"}`,
      )
      .then((res) => {
        const stores = res.data.data?.stores || [];

        const suppliers = res.data.data?.suppliers || [];

        setBranchList(stores);

        setSupplierList(suppliers);
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);

        toast.error("Failed to load dropdowns");
      });
  }, [branchType, storedBranchId]);

  /* =========================================================
     FORMAT DATE FOR API
  ========================================================= */

  const formatDate = (dateStr) => {
    if (!dateStr) {
      return "";
    }

    const [year, month, day] = dateStr.split("-");

    return `${day}/${month}/${year}`;
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    if (!store) {
      toast.warn("Please select a Store");

      return;
    }

    setLoading(true);

    const params = {
      supplierid: supplier || "",

      grnno: grnNo || "%",

      fromdate: fromDate ? formatDate(fromDate) : "",

      todate: toDate ? formatDate(toDate) : "",

      status: status || "",

      storeid: "",

      projectid: projectCode || "",

      pur_type: branchType === "HO" ? "H" : "B",

      branchtype: branchType === "HO" ? "HO" : "BRANCH",

      ddl_branch: "Select",

      session_branchid: storedBranchId || "",
    };

    axios
      .get("https://nissi-india.com/api/grn_rpt", {
        params,
      })
      .then((res) => {
        const rows = res.data?.data || [];

        setData(rows);

        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("GRN fetch failed", err);

        console.error("Request params:", params);

        toast.error("Failed to fetch GRN listing");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     PRINT REPORT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const params = {
      supplierid: supplier || "",

      grnno: grnNo || "%",

      fromdate: fromDate ? formatDate(fromDate) : "",

      todate: toDate ? formatDate(toDate) : "",

      status: status || "",

      storeid: "",

      projectid: projectCode || "",

      pur_type: branchType === "HO" ? "H" : "B",

      branchtype: branchType === "HO" ? "HO" : "BRANCH",

      ddl_branch: "Select",

      session_branchid: storedBranchId || "",
    };

    navigate("/inventory/GRN_AllPrint", {
      state: {
        params,
      },
    });
  };

  /* =========================================================
     VIEW INVOICE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleViewInvoice = (invoiceNo) => {
    axios
      .get("https://www.nissi-india.com/api/grnInvoice_documentsRpt", {
        params: {
          invoiceno: invoiceNo,
        },
      })
      .then((res) => {
        if (res.data?.fileUrl) {
          window.open(res.data.fileUrl, "_blank");
        } else {
          toast.info("Invoice data loaded. Check console.");
        }
      })
      .catch((err) => {
        console.error("Invoice fetch failed", err);

        toast.error("Failed to load invoice");
      });
  };

  /* =========================================================
     BRANCH TYPE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleBranchTypeChange = (value) => {
    setBranchType(value);
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setFromDate("");

    setToDate("");

    setGrnNo("");

    setProjectCode("");

    setStatus("");

    setStore("");

    setSupplier("");

    setData([]);
  };

  /* =========================================================
     STATUS COLOR
  ========================================================= */

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case "Waiting":
        return isDark
          ? "bg-yellow-900/30 text-yellow-300"
          : "bg-yellow-100 text-yellow-700";

      case "Cancel":
        return isDark
          ? "bg-red-900/30 text-red-300"
          : "bg-red-100 text-red-700";

      case "Closed":
        return isDark
          ? "bg-green-900/30 text-green-300"
          : "bg-green-100 text-green-700";

      default:
        return isDark
          ? "bg-gray-700 text-gray-300"
          : "bg-gray-100 text-gray-700";
    }
  };

  /* =========================================================
     CURRENCY FORMAT
  ========================================================= */

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>
      {/* =====================================================
          PAGE HEADER
          SAME AS DEPARTMENT MASTER
      ===================================================== */}

      <HeaderBar
        title={<span className="font-dmserif text-3xl">GRN Report</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* ===================================================
            FILTER FORM CARD
            MATCHES DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* ===============================================
              FORM HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >
            {/* LEFT */}

            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-5 h-5 text-white" />
              </div>

              <div>
                <h3
                  className={`text-lg font-bold ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  Goods Receipt Note Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  Search and review goods receipt note records
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* EXPORT - kept as existing visual functionality */}
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload size={14} />
                Export
              </button>

              {/* HIDE / SHOW FORM */}
              <button
                type="button"
                onClick={() => setShowForm(!showForm)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter size={14} />

                {showForm ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FORM BODY
          =============================================== */}

          {showForm && (
            <div className="p-6">
              {/* =============================================
                  BRANCH TYPE
              ============================================= */}

              <div className="mb-6">
                <label className={labelClass}>Branch Type</label>

                <div className="flex flex-wrap gap-2">
                  {/* HEAD OFFICE */}

                  <button
                    type="button"
                    onClick={() => handleBranchTypeChange("HO")}
                    disabled={
                      storedBranch !== "HEAD-OFFICE" &&
                      storedBranchType !== "HO"
                    }
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      branchType === "HO"
                        ? `bg-${baseColor}-${baseShade} text-white shadow`
                        : isDark
                          ? "bg-slate-800 text-slate-300 border border-slate-600"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                    } ${
                      storedBranch !== "HEAD-OFFICE" &&
                      storedBranchType !== "HO"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Head Office
                  </button>

                  {/* BRANCH */}

                  <button
                    type="button"
                    onClick={() => handleBranchTypeChange("Branch")}
                    disabled={
                      storedBranch === "HEAD-OFFICE" ||
                      storedBranchType === "HO"
                    }
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      branchType === "Branch"
                        ? `bg-${baseColor}-${baseShade} text-white shadow`
                        : isDark
                          ? "bg-slate-800 text-slate-300 border border-slate-600"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                    } ${
                      storedBranch === "HEAD-OFFICE" ||
                      storedBranchType === "HO"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Branch
                  </button>
                </div>
              </div>

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >
                {/* FROM DATE */}

                <div>
                  <label className={labelClass}>From Date</label>

                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label className={labelClass}>To Date</label>

                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* GRN NO */}

                <div>
                  <label className={labelClass}>GRN No</label>

                  <input
                    type="text"
                    value={grnNo}
                    onChange={(e) => setGrnNo(e.target.value)}
                    placeholder="Enter GRN number"
                    className={inputClass}
                  />
                </div>

                {/* PROJECT CODE */}

                <div>
                  <label className={labelClass}>Project Code</label>

                  <select
                    value={projectCode}
                    onChange={(e) => setProjectCode(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Project Code</option>

                    <option value="P1">Project 1</option>

                    <option value="P2">Project 2</option>
                  </select>
                </div>

                {/* STATUS */}

                <div>
                  <label className={labelClass}>Status</label>

                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Status</option>

                    <option value="Waiting">Waiting</option>

                    <option value="Cancel">Cancel</option>

                    <option value="Closed">Closed</option>
                  </select>
                </div>

                {/* SUPPLIER */}

                <div>
                  <label className={labelClass}>Supplier</label>

                  <select
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Supplier</option>

                    {supplierList.map((s) => (
                      <option key={s.fld_supplierid} value={s.fld_supplierid}>
                        {s.fld_suppliername}
                      </option>
                    ))}
                  </select>
                </div>

                {/* STORE */}

                <div>
                  <label className={labelClass}>
                    Store <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Store</option>

                    {branchList.map((b) => (
                      <option key={b.fld_storeid} value={b.fld_storeid}>
                        {b.fld_storename}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* =============================================
                  FORM BUTTONS
                  SAME AS DEPARTMENT MASTER POSITIONING
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >
                {/* CLEAR */}

                <button
                  type="button"
                  onClick={clearFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Plus size={16} className="rotate-45" />
                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={handlePrint}
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-semibold
                    text-green-700
                    bg-green-100
                    hover:bg-green-200
                    rounded-xl
                    transition
                    ${
                      isDark
                        ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                        : ""
                    }
                  `}
                >
                  <FaPrint size={15} />
                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span
                      className="
                        w-4
                        h-4
                        border-2
                        border-white
                        border-t-transparent
                        rounded-full
                        animate-spin
                      "
                    />
                  ) : (
                    <FaSearch size={14} />
                  )}

                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            TABLE CARD
            MATCHES DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* =================================================
              TABLE HEADER
          ================================================= */}

          <div
            className="
              p-6
              border-b
              border-gray-200
              dark:border-gray-700
            "
          >
            <div
              className="
                flex
                flex-col
                sm:flex-row
                items-start
                sm:items-center
                justify-between
                gap-4
              "
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">GRN List</h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>
            </div>
          </div>

          {/* =================================================
              TABLE LOADING
          ================================================= */}

          {loading ? (
            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                py-16
              "
            >
              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                Loading GRN records...
              </p>
            </div>
          ) : (
            <>
              {/* =============================================
                  TABLE
              ============================================= */}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  {/* HEADER */}

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        S.No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        GRN No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Supplier
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Inv No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Inv Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Amount
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Status
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Print
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Invoice
                      </th>
                    </tr>
                  </thead>

                  {/* BODY */}

                  <tbody
                    className={`divide-y ${
                      isDark ? "divide-gray-600" : "divide-gray-200"
                    }`}
                  >
                    {data.length > 0 ? (
                      data.map((grn, index) => (
                        <tr
                          key={grn.fld_grnid || index}
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          {/* S NO */}

                          <td className="px-4 py-3 font-medium">{index + 1}</td>

                          {/* GRN NO */}

                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {grn.fld_grnno || "-"}
                            </span>
                          </td>

                          {/* DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {grn.fld_date || "-"}
                          </td>

                          {/* SUPPLIER */}

                          <td className="px-4 py-3 font-medium">
                            {grn.fld_suppliername || "-"}
                          </td>

                          {/* INVOICE NO */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {grn.invno || "-"}
                          </td>

                          {/* INVOICE DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {grn.fld_InvDate || "-"}
                          </td>

                          {/* AMOUNT */}

                          <td
                            className={`px-4 py-3 whitespace-nowrap font-semibold ${
                              isDark ? "text-green-300" : "text-green-700"
                            }`}
                          >
                            {formatCurrency(grn.TotalValueNew)}
                          </td>

                          {/* STATUS */}

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                                grn.Status,
                              )}`}
                            >
                              {grn.Status || "Pending"}
                            </span>
                          </td>

                          {/* PRINT */}

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                window.open(
                                  `/inventory/GoodsReceiptNote-Print?grnid=${grn.fld_grnid}`,
                                  "_blank",
                                )
                              }
                              className={`p-1.5 rounded-full transition ${
                                isDark
                                  ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                  : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                              }`}
                              title="Print GRN"
                            >
                              <FaPrint className="w-4 h-4" />
                            </button>
                          </td>

                          {/* VIEW INVOICE */}

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleViewInvoice(grn.invno)}
                              className={`p-1.5 rounded-full transition ${
                                isDark
                                  ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                  : "bg-green-100 text-green-600 hover:bg-green-200"
                              }`}
                              title="View Invoice"
                            >
                              <FaFileInvoice className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      /* =====================================
                         EMPTY STATE
                      ===================================== */

                      <tr>
                        <td
                          colSpan={10}
                          className="
                            px-4
                            py-14
                            text-center
                            text-gray-500
                          "
                        >
                          <div
                            className="
                              flex
                              flex-col
                              items-center
                              gap-2
                            "
                          >
                            <FaEye size={40} className="text-gray-400" />

                            <span>No GRN records found</span>

                            <span
                              className={`text-xs ${
                                isDark ? "text-slate-500" : "text-slate-400"
                              }`}
                            >
                              Use the filters above to search for GRN records
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default GRNReport;
